import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { GALLERY, GALLERY_CATEGORIES } from "@/lib/data";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import { useLenis } from "@/hooks/useLenis";

const FRAMES = [
  "rounded-[2rem]",
  "rounded-t-[999px] rounded-b-2xl",
  "rounded-2xl rounded-tr-[5rem]",
  "rounded-2xl rounded-bl-[5rem]",
  "rounded-[2rem]",
  "rounded-2xl rounded-tl-[5rem] rounded-br-[5rem]",
  "rounded-b-[999px] rounded-t-2xl",
  "rounded-2xl rounded-tr-[5rem]",
  "rounded-[2rem]",
];

export const Gallery = () => {
  const [filter, setFilter] = useState("semua");
  const [active, setActive] = useState(null);
  const lenis = useLenis();
  const items = filter === "semua" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  useEffect(() => {
    if (!lenis) return;
    if (active !== null) lenis.stop();
    else lenis.start();
  }, [active, lenis]);

  const step = (dir) => setActive((i) => (i + dir + items.length) % items.length);

  return (
    <section id="galeri" className="relative bg-sand-50 py-28 lg:py-36" data-testid="gallery-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="Galeri"
            title={
              <>
                Cahaya senja, <em>kubah hijau,</em> dan deburan ombak.
              </>
            }
          />
          <Reveal delay={0.15}>
            <div className="flex flex-wrap gap-2" role="tablist" data-testid="gallery-filters">
              {GALLERY_CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={filter === c.key}
                  onClick={() => setFilter(c.key)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-[background-color,color,border-color] duration-300 ${
                    filter === c.key ? "border-navy bg-navy text-sand-50" : "border-navy/15 text-navy hover:border-navy"
                  }`}
                  data-testid={`gallery-filter-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-14 grid grid-flow-dense grid-cols-2 gap-4 auto-rows-[150px] md:grid-cols-4 md:auto-rows-[220px]" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {items.map((g, i) => (
              <motion.figure
                key={g.src}
                layout
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, ease: EASE }}
                className={`group relative cursor-zoom-in overflow-hidden bg-navy ${g.span} ${FRAMES[i % FRAMES.length]}`}
                onClick={() => setActive(i)}
                data-testid={`gallery-item-${i}`}
              >
                <img src={g.src} alt={g.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-navy/0 transition-colors duration-500 group-hover:bg-navy/40" />
                <span className="absolute right-4 top-4 grid h-9 w-9 translate-y-2 place-items-center rounded-full bg-sand-50 text-navy opacity-0 transition-[opacity,transform] duration-400 group-hover:translate-y-0 group-hover:opacity-100">
                  <Maximize2 className="h-4 w-4" />
                </span>
                <figcaption className="absolute inset-x-5 bottom-4 translate-y-3 text-sm text-sand-50 opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {g.alt}
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={active !== null} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-6xl border-0 bg-transparent p-0 shadow-none [&>button]:right-2 [&>button]:top-2 [&>button]:rounded-full [&>button]:bg-sand-50 [&>button]:p-2 [&>button]:text-navy [&>button]:opacity-100" data-testid="gallery-lightbox">
          {active !== null && items[active] && (
            <figure className="relative">
              <DialogTitle className="sr-only">{items[active].alt}</DialogTitle>
              <img src={items[active].src} alt={items[active].alt} className="max-h-[80vh] w-full rounded-3xl object-contain" data-testid="gallery-lightbox-image" />
              <figcaption className="mt-4 flex items-center justify-between gap-4 text-sand-50">
                <span className="text-sm">{items[active].alt}</span>
                <span className="flex gap-2">
                  <button onClick={() => step(-1)} className="grid h-10 w-10 place-items-center rounded-full bg-sand-50 text-navy transition-colors hover:bg-gold" aria-label="Sebelumnya" data-testid="gallery-lightbox-prev">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button onClick={() => step(1)} className="grid h-10 w-10 place-items-center rounded-full bg-sand-50 text-navy transition-colors hover:bg-gold" aria-label="Berikutnya" data-testid="gallery-lightbox-next">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </span>
              </figcaption>
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
